import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { languages } from '@/i18n';

const BASE_URL = 'https://katado.design';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  path?: string;
}

const SEOHead = ({ title, description, keywords, path = '' }: SEOHeadProps) => {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;

  const defaultTitle = t('seo.title', 'International Luxury Landscape Architecture | Katado Design');
  const defaultDescription = t('seo.description', 'Online luxury landscape architecture studio specializing in Japandi-inspired garden design. International projects, thoughtful planting, and refined outdoor spaces, all delivered remotely.');
  const defaultKeywords = t('seo.keywords', 'luxury landscape architecture, online landscape design, garden design, outdoor design, japandi, international, landscape architect, modern garden, planting design, refined outdoor spaces');

  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const canonicalUrl = `${BASE_URL}${path}`;

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    }

    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', pageKeywords);

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', canonicalUrl);
    }

    // Update Open Graph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', pageDescription);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);

    // Update Twitter
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', pageTitle);

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', pageDescription);

    // Update HTML lang attribute
    document.documentElement.lang = currentLang;

    // Add/Update hreflang tags
    // Remove existing hreflang links
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());

    // Add hreflang for each language
    languages.forEach(lang => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang.code;
      link.href = `${BASE_URL}${path}?lang=${lang.code}`;
      document.head.appendChild(link);
    });

    // Add x-default hreflang
    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.hreflang = 'x-default';
    xDefault.href = canonicalUrl;
    document.head.appendChild(xDefault);

  }, [pageTitle, pageDescription, pageKeywords, canonicalUrl, currentLang, path]);

  return null;
};

export default SEOHead;
